import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { ButtonLink } from "design-system/atoms/Button"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { isFilled, isNil } from "lopermut"
import { ArrowRight, ChevronRight } from "lucide-react"
import { createContext, useContext, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CampaignLogResponseDataSchema } from "schema"
import z from "zod"
import { CampaignLogKindBadge } from "./CampaignLogKindBadge"
import { Row } from "./Row"

const columnHelper = createColumnHelper<Features, z.infer<typeof CampaignLogResponseDataSchema>>()

type TableContextType = {
  expandedRows: Set<string>
}

const TableContext = createContext<TableContextType>({ expandedRows: new Set() })

const ChevronCell = ({ rowId, isExpandable }: { rowId: string; isExpandable: boolean }) => {
  const { expandedRows } = useContext(TableContext)

  if (!isExpandable) return null

  return (
    <ChevronRight
      className={cn(
        "size-4.5 transition-transform duration-200",
        expandedRows.has(rowId) ? "rotate-90" : "rotate-0",
      )}
    />
  )
}

type Props = {
  id: string
  data: z.infer<typeof CampaignLogResponseDataSchema>[]
  isPending: boolean
}

export const CampaignLogsTable = ({ id, data, isPending }: Props) => {
  const { t } = useTranslation()
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const columns = useMemo(() => {
    return columnHelper.columns([
      columnHelper.accessor("id", {
        header: () => null,
        cell: (props) => {
          const isExpandable = !isNil(props.row.original.log)
          return <ChevronCell rowId={props.row.original.id} isExpandable={isExpandable} />
        },
        meta: {
          className: "w-4",
        },
      }),
      columnHelper.accessor("kind", {
        header: t("campaigns.detail.logs.table.kind"),
        cell: (props) => {
          const value = props.getValue()
          return <CampaignLogKindBadge kind={value} />
        },
      }),
      columnHelper.accessor("customer", {
        header: t("campaigns.detail.logs.table.customer"),
        cell: (props) => {
          const value = props.getValue()
          const hasName = isFilled(value?.name)
          return (
            <span className={cn(hasName ? "font-medium" : "text-muted-foreground")}>
              {hasName
                ? value?.name
                : value?.email || value?.tentativeEmail || t("customers.profile.noName")}
            </span>
          )
        },
      }),
      columnHelper.accessor("createdAt", {
        header: t("campaigns.detail.logs.table.createdAt"),
        cell: (props) => {
          const value = props.getValue()
          return <DateDisplay date={value} />
        },
        meta: {
          align: "right",
        },
      }),
      columnHelper.display({
        id: "goToConversationOrCustomer",
        header: () => null,
        cell: (props) => {
          if (props.row.original.conversationId && props.row.original.customerId) {
            return (
              <ButtonLink
                variant="outline"
                size="sm"
                to="/campaigns/$id/activity/$customerId"
                params={{ id, customerId: props.row.original.customerId }}
                search={{
                  section: "logs",
                  conversationId: props.row.original.conversationId,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <span>{t("campaigns.detail.logs.table.goToConversation")}</span>
                <ArrowRight size={16} />
              </ButtonLink>
            )
          }

          if (props.row.original.customerId) {
            return (
              <ButtonLink
                variant="outline"
                size="sm"
                to="/campaigns/$id/activity/$customerId"
                params={{ id, customerId: props.row.original.customerId }}
                search={{ section: "logs" }}
                onClick={(e) => e.stopPropagation()}
              >
                <span>{t("campaigns.detail.logs.table.goToCustomer")}</span>
                <ArrowRight size={16} />
              </ButtonLink>
            )
          }

          return null
        },
        meta: {
          align: "right",
        },
      }),
    ])
  }, [t, id])

  function handleToggle(id: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev)

      if (prev.has(id)) next.delete(id)
      else next.add(id)

      return next
    })
  }

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (!data || data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("campaigns.detail.logs.table.empty")}
        />
      )
    }

    return table
      .getRowModel()
      .rows.map((row) => (
        <Row
          key={row.original.id}
          row={row}
          expandedRows={expandedRows}
          onToggleExpand={handleToggle}
        />
      ))
  }

  return (
    <TableContext.Provider value={{ expandedRows }}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} {...header.column.columnDef.meta}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
    </TableContext.Provider>
  )
}
